import moment from 'moment';
import _ from 'lodash';
import { REPORTS_DATE_INFIMUM } from './../../../common/constants';
import { videoAdUnits } from './../../../server/enums';

const BREAKDOWN_SERIES_KEY_MAP = {
  app: 'appName',
  placement: 'placementName',
  adUnits: 'adUnitName',
  platform: 'os',
};

const FILTER_OPTION_KEY_MAP = {
  apps: '_id',
  placements: '_id',
  adUnits: 'type',
};

const FILTER_REF_OPTION_KEY = {
  apps: 'appId',
  placements: 'placementId',
  adUnits: 'adType',
};
function getMoments(period) {
  const moments = [];
  if (period.name === 'today' || period.name === 'yesterday' || period.from === period.to) {
    for (let hour = 0; hour < 25; hour += 1) {
      moments.push(`${hour}:00`);
    }
  } else {
    let current = new Date(period.from);
    const last = moment(period.to, 'MM/DD/YYYY').add(1, 'day').toDate();
    while (moment(current).isBefore(last)) {
      moments.push(moment(current).format('MM/DD/YYYY'));
      current = moment(current).add(1, 'day');
    }
  }

  return moments;
}

export function setMetrics(state, { metrics }) {
  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      metrics: {
        ...state.dashboard.metrics,
        active: metrics,
      },
    },
  };
}

export function setInsights(state, { insights }) {
  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      insights: {
        ...state.dashboard.insights,
        active: insights,
      },
    },
  };
}

export function fetchPerformance(state, { filter }) {
  const isVideoFilter = filter.adUnits.every((adUnit) => videoAdUnits.includes(adUnit));
  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      isFetching: true,
      filter: filter || state.dashboard.filter,
      metrics: {
        active: 'earnings',
        visible: isVideoFilter
          ? ['earnings', 'ecpm', 'fillRate', 'numberOfImpressions', 'cvvs']
          : ['earnings', 'ecpm', 'numberOfImpressions', 'numberOfClicks', 'numberOfLeads'],
      },
    },
  };
}

export function fetchTablePerformance(state) {
  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      isFetching: true,
    },
  };
}

export function filterTablePerformance(state, { page, itemsPerPage, filter }) {
  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      table: {
        ...state.dashboard.table,
        filter,
        currentPage: page,
        itemsPerPage,
      },
      isFetching: true,
    },
  };
}

export function setFilter(state, { filterKey, selectedOptions }) {
  if (filterKey === 'countries') {
    return {
      ...state,
      dashboard: {
        ...state.dashboard,
        filter: {
          ...state.dashboard.filter,
          countries: selectedOptions,
        },
      },
    };
  }

  const optionsKey = FILTER_OPTION_KEY_MAP[filterKey];
  const emptyOptions = state.dashboard[filterKey]
    .filter((val) => !selectedOptions.find((option) => val[optionsKey] === option))
    .map((val) => val[optionsKey]);

  let filterCondition = (ref) => !emptyOptions.find((option) =>
    ref[FILTER_REF_OPTION_KEY[filterKey]] === option
  );
  if (filterKey === 'adUnits') {
    const appIds = state.dashboard.filter.apps;
    filterCondition = (ref) => !emptyOptions.find((option) =>
      ref[FILTER_REF_OPTION_KEY[filterKey]] === option
    ) && appIds.find(x => x === ref.appId);
  }

  const filteredFilterReference = state.dashboard.filterReferences
    .filter(filterCondition);

  let displayedFilters = state.dashboard.displayedFilters;
  if (filterKey === 'apps') {
    displayedFilters = {
      ...state.dashboard.displayedFilters,
      adUnits: _.uniqBy(state.dashboard.adUnits
        .filter(({ type }) => filteredFilterReference.find((ref) => ref.adType === type)), 'type'),
      placements: state.dashboard.placements
        .filter(({ _id }) => filteredFilterReference.find((ref) => ref.placementId === _id)),
    };
  }

  if (filterKey === 'adUnits') {
    displayedFilters = {
      ...state.dashboard.displayedFilters,
      placements: state.dashboard.placements
        .filter(({ _id }) => filteredFilterReference.find((ref) => ref.placementId === _id)),
    };
  }

  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      displayedFilters: {
        ...state.dashboard.displayedFilters,
        ...displayedFilters,
      },
      filter: {
        ...state.dashboard.filter,
        apps: _.uniq(filteredFilterReference.map(({ appId }) => appId)),
        adUnits: _.uniq(filteredFilterReference.map(({ adType }) => adType)),
        placements: _.uniq(filteredFilterReference.map(({ placementId }) => placementId)),
      },
    },
  };
}

export function setBreakdown(state, { breakdown }) {
  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      breakdown,
    },
  };
}

export function setPeriod(state, { period }) {
  const isCorrectPeriod = moment(period.from, 'MM/DD/YYYY')
    .isSameOrAfter(moment(REPORTS_DATE_INFIMUM));
  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      period,
      isCorrectPeriod,
    },
  };
}

export function fetchPerformanceSuccess(state, { data, breakdown, period }) {
  const moments = getMoments(period);

  const isOneDayThisMonth = (
    period === 'thismonth' &&
    moment(new Date(period.from)).add(1, 'day').isSame(new Date(period.to))
  );

  const metrics = _.reduce(data, (acc, value) =>
      ({
        numberOfImpressions: acc.numberOfImpressions + value.numberOfImpressions,
        numberOfClicks: acc.numberOfClicks + value.numberOfClicks,
        numberOfLeads: acc.numberOfLeads + value.numberOfLeads,
        numberOfRequests: acc.numberOfRequests + value.numberOfRequests,
        numberOfUsefulRequests: acc.numberOfUsefulRequests + value.numberOfUsefulRequests,
        cvvs: acc.cvvs + value.cvvs,
        earnings: acc.earnings + value.earnings,
      })
    , {
      numberOfImpressions: 0,
      numberOfClicks: 0,
      numberOfLeads: 0,
      numberOfUsefulRequests: 0,
      numberOfRequests: 0,
      cvvs: 0,
      earnings: 0,
    });

  metrics.ecpm = metrics.numberOfImpressions === 0
    ? undefined
    : Math.round(((metrics.earnings / metrics.numberOfImpressions) * 1000 || 0) * 100) / 100;
  metrics.fillRate = ((metrics.numberOfUsefulRequests /
    metrics.numberOfRequests) * 100 || 0).toFixed(2);

  metrics.earnings = Math.ceil(metrics.earnings * 100) / 100;

  const series = {};
  _.forEach(data, (d) => {
    const key = d[BREAKDOWN_SERIES_KEY_MAP[breakdown]];
    series[key] = [];
    if (d.series) {
      _.forEach(moments, (m) => {
        const point = _.find(d.series, (p) => m === p.date);

        if (point) {
          series[key].push(point);
        } else if (isOneDayThisMonth) {
          series[key].push(data);
        } else {
          series[key].push({});
        }
      });
    }
  });

  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      ...metrics,
      isFetching: false,
      chart: {
        moments,
        series,
      },
      breakdown,
      period,
    },
  };
}

export function fetchTablePerformanceSuccess(state, { data, page, sort }) {
  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      isFetching: false,
      table: {
        ...state.dashboard.table,
        rows: data.rows,
        numberOfPages: data.numberOfPages,
        currentPage: page,
        sort,
      },
    },
  };
}

export function fetchFiltersDataSuccess(state, { adUnits, applications,
  placements, filterReferences }) {
  const displayedFilters = state.dashboard.displayedFilters;

  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      filterReferences,
      displayedFilters: {
        apps: displayedFilters.apps.length ? displayedFilters.apps : applications,
        adUnits: displayedFilters.adUnits.length ? _.uniqBy(displayedFilters.adUnits, 'type') :
          _.uniqBy(adUnits, 'type'),
        placements: displayedFilters.placements.length ? displayedFilters.placements : placements,
      },
      filter: {
        ...state.dashboard.filter,
        apps: state.dashboard.filter.apps || applications.map(({ _id }) => _id),
        adUnits: state.dashboard.filter.adUnits || _.uniq(adUnits.map(({ type }) => type)),
        placements: state.dashboard.filter.placements || placements.map(({ _id }) => _id),
      },
      apps: applications,
      adUnits,
      placements,
    },
  };
}

export function changeShouldSidebarBeShown(state, payload) {
  return {
    ... state,
    dashboard: {
      ...state.dashboard,
      shouldSidebarBeShown: payload,
    },
  };
}

export function fetchInsights(state) {
  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      isFetching: true,
    },
  };
}

export function fetchInsightsSuccess(state, { data, period }) {
  const topGeo = data.topGeoData.length ? data.topGeoData[0].country : undefined;
  const topApp = data.topAppData.length ? data.topAppData[0] : undefined;
  const impressionsPerUser = data.performanceData ? data.performanceData.totalImpressionsPerUser : undefined;
  const adEngagement = data.performanceData ? data.performanceData.totalAdEngagement : undefined;
  const arpau = data.performanceData ? data.performanceData.totalARPAU : undefined;

  const moments = getMoments(period);

  const series = {};
  if (data.performanceData && !_.isEmpty(data.performanceData)) {
    data.performanceData.seriesData.forEach((d) => {
      const key = d._id;
      series[key] = [];
      moments.forEach((m) => {
        const point = d.series.find((p) => m === p.date);

        if (point) {
          series[key].push(point);
        } else {
          series[key].push({});
        }
      });
    });
  }

  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      isFetching: false,
      insights: {
        ...state.dashboard.insights,
        topGeo,
        topGeoChartData: data.topGeoData,
        topApp,
        topAppChartData: data.topAppData,
        impressionsPerUser,
        adEngagement,
        arpau,
        periodChartData: {
          moments,
          series,
        },
      },
    },
  };
}



/** WEBPACK FOOTER **
 ** ./js/core/dashboard.js
 **/